import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Checkbox,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
} from "@mui/material";
import { useAuth } from "../AuthContext";
import useAuthenticatedFetch from "../hooks/useAuthenticatedFetch";
import { useTranslation } from "react-i18next";
import { parseZmanimSettings } from "../utils/zmanimSettingsUtils";

const ZmanimEditor = ({ onIsDirtyChange }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [zmanimSettings, setZmanimSettings] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const fetchWithAuth = useAuthenticatedFetch();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchWithAuth(
          `${process.env.REACT_APP_BASE_URL}/community/getCommunityById/${user.communityId}`
        );
        const data = await response.json();
        setZmanimSettings(parseZmanimSettings(data.zmanimSettings));
      } catch (error) {
        console.error(error);
        setErrorMessage(t("zmanim.errors.fetchError"));
      }
    };

    fetchData();
  }, [user.communityId]);

  useEffect(() => {
    if (onIsDirtyChange) {
      onIsDirtyChange(isDirty);
    }
  }, [isDirty, onIsDirtyChange]);

  const handleUpdateZman = (index, field, value) => {
    const updatedZmanim = [...zmanimSettings];
    updatedZmanim[index][field] = value;
    setZmanimSettings(updatedZmanim);
    setIsDirty(true);
  };

  const handleSave = async () => {
    try {
      await fetchWithAuth(
        `${process.env.REACT_APP_BASE_URL}/community/updateZmanimSettings/${user.communityId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ zmanimSettings }),
        }
      );
      setSuccessMessage(t("zmanim.success.save"));
      setIsDirty(false);
    } catch (error) {
      console.error(error);
      setErrorMessage(t("zmanim.errors.saveError"));
    }
  };

  return (
    <Box sx={{ mt: 1, width: "100%" }}>
      <Typography variant="h4" gutterBottom>
        {t("zmanim.title")}
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '40%' }}>{t("zmanim.name")}</TableCell>
              <TableCell sx={{ width: '20%' }}>{t("zmanim.customName")}</TableCell>
              <TableCell sx={{ width: '10%' }}>{t("zmanim.displayed")}</TableCell>
              <TableCell sx={{ width: '10%' }}>{t("zmanim.offSet")}</TableCell>
              <TableCell sx={{ width: '10%' }}>{t("zmanim.degree")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {zmanimSettings.map((zman, index) => (
              <TableRow key={zman.name}>
                <TableCell>
                  <Box>
                    <Typography>{zman.name}</Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {t(`zmanim.descriptions.${zman.name.toLowerCase().replace(/\s+/g, '')}`)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <TextField
                    value={zman.customName}
                    onChange={(e) =>
                      handleUpdateZman(index, "customName", e.target.value)
                    }
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    checked={zman.displayed}
                    onChange={(e) =>
                      handleUpdateZman(index, "displayed", e.target.checked)
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={zman.offSet}
                    onChange={(e) =>
                      handleUpdateZman(index, "offSet", e.target.value)
                    }
                    fullWidth
                    type="number"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={zman.degree}
                    onChange={(e) =>
                      handleUpdateZman(index, "degree", e.target.value)
                    }
                    fullWidth
                    type="number"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t("zmanim.save")}
        </Button>
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage("")}
        message={errorMessage}
      />
    </Box>
  );
};

export default ZmanimEditor; 